/**
 * Reslice MPR oblicuo: muestreo nearest / trilineal en espacio de índices.
 */

/**
 * @param {Int16Array|null|undefined} slice
 * @param {number} x
 * @param {number} y
 * @param {number} cols
 * @param {number} rows
 */
function sampleSliceNearest(slice, x, y, cols, rows) {
  if (!slice) return 0
  const xi = Math.max(0, Math.min(cols - 1, Math.round(x)))
  const yi = Math.max(0, Math.min(rows - 1, Math.round(y)))
  return slice[xi + cols * yi] | 0
}

/**
 * @param {(z:number)=>Int16Array|null} getSlice
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {number} cols
 * @param {number} rows
 * @param {number} zDim
 */
export function sampleNearest(getSlice, x, y, z, cols, rows, zDim) {
  const zi = Math.max(0, Math.min(zDim - 1, Math.round(z)))
  return sampleSliceNearest(getSlice(zi), x, y, cols, rows)
}

/**
 * @param {(z:number)=>Int16Array|null} getSlice
 * @param {number} x
 * @param {number} y
 * @param {number} z
 * @param {number} cols
 * @param {number} rows
 * @param {number} zDim
 */
export function sampleTrilinear(getSlice, x, y, z, cols, rows, zDim) {
  const x0 = Math.floor(x)
  const y0 = Math.floor(y)
  const z0 = Math.floor(z)
  const x1 = x0 + 1
  const y1 = y0 + 1
  const z1 = z0 + 1
  const fx = x - x0
  const fy = y - y0
  const fz = z - z0

  const clampX = (v) => Math.max(0, Math.min(cols - 1, v))
  const clampY = (v) => Math.max(0, Math.min(rows - 1, v))
  const clampZ = (v) => Math.max(0, Math.min(zDim - 1, v))

  const xa = clampX(x0)
  const xb = clampX(x1)
  const ya = clampY(y0)
  const yb = clampY(y1)
  const za = clampZ(z0)
  const zb = clampZ(z1)

  const s0 = getSlice(za)
  const s1 = getSlice(zb)
  if (!s0 && !s1) return 0

  const c000 = s0 ? s0[xa + cols * ya] : 0
  const c100 = s0 ? s0[xb + cols * ya] : 0
  const c010 = s0 ? s0[xa + cols * yb] : 0
  const c110 = s0 ? s0[xb + cols * yb] : 0
  const c001 = s1 ? s1[xa + cols * ya] : 0
  const c101 = s1 ? s1[xb + cols * ya] : 0
  const c011 = s1 ? s1[xa + cols * yb] : 0
  const c111 = s1 ? s1[xb + cols * yb] : 0

  const c00 = c000 * (1 - fx) + c100 * fx
  const c10 = c010 * (1 - fx) + c110 * fx
  const c01 = c001 * (1 - fx) + c101 * fx
  const c11 = c011 * (1 - fx) + c111 * fx
  const c0 = c00 * (1 - fy) + c10 * fy
  const c1 = c01 * (1 - fy) + c11 * fy
  return (c0 * (1 - fz) + c1 * fz) | 0
}

function projectSamples(proj, values) {
  if (!values.length) return 0
  if (proj === 'mip') {
    let m = values[0]
    for (let i = 1; i < values.length; i++) if (values[i] > m) m = values[i]
    return m | 0
  }
  if (proj === 'minip') {
    let m = values[0]
    for (let i = 1; i < values.length; i++) if (values[i] < m) m = values[i]
    return m | 0
  }
  if (proj === 'avg') {
    let s = 0
    for (let i = 0; i < values.length; i++) s += values[i]
    return (s / values.length) | 0
  }
  return values[Math.floor(values.length / 2)] | 0
}

/**
 * Extrae un plano oblicuo al centro dado.
 * Opcional: thick slab a lo largo de `n` (MIP / MinIP / Avg).
 * @param {{
 *   getSlice: (z:number)=>Int16Array|null,
 *   cols: number,
 *   rows: number,
 *   zDim: number,
 *   center: {x:number,y:number,z:number},
 *   u: [number,number,number],
 *   v: [number,number,number],
 *   n?: [number,number,number],
 *   outCols: number,
 *   outRows: number,
 *   mode?: 'nearest'|'trilinear',
 *   slabMode?: 'thin'|'mip'|'minip'|'avg',
 *   halfWidth?: number,
 *   dest?: Int16Array,
 * }} args
 * @returns {Int16Array}
 */
export function resliceObliquePlane(args) {
  const cols = Math.max(1, Number(args.cols) || 1)
  const rows = Math.max(1, Number(args.rows) || 1)
  const zDim = Math.max(1, Number(args.zDim) || 1)
  const outCols = Math.max(1, Number(args.outCols) || 1)
  const outRows = Math.max(1, Number(args.outRows) || 1)
  const need = outCols * outRows
  const dest =
    args.dest && args.dest.length === need ? args.dest : new Int16Array(need)
  const cx = (outCols - 1) / 2
  const cy = (outRows - 1) / 2
  const u = args.u || [1, 0, 0]
  const v = args.v || [0, 1, 0]
  const n = args.n || [0, 0, 1]
  const ox = Number(args.center && args.center.x) || 0
  const oy = Number(args.center && args.center.y) || 0
  const oz = Number(args.center && args.center.z) || 0
  const getSlice = args.getSlice
  const useTri = String(args.mode || 'nearest').toLowerCase() === 'trilinear'
  const sample = useTri ? sampleTrilinear : sampleNearest
  const kMax = Math.max(0, Math.round(Number(args.halfWidth) || 0))
  const slabRaw = String(args.slabMode || 'thin').toLowerCase()
  const useThin = kMax === 0 || slabRaw === 'thin'
  const proj = useThin ? 'thin' : slabRaw
  const scratch = useThin ? null : []

  for (let row = 0; row < outRows; row++) {
    const dr = row - cy
    for (let col = 0; col < outCols; col++) {
      const dc = col - cx
      const x0 = ox + dc * u[0] + dr * v[0]
      const y0 = oy + dc * u[1] + dr * v[1]
      const z0 = oz + dc * u[2] + dr * v[2]
      const i = col + outCols * row
      if (useThin) {
        dest[i] = sample(getSlice, x0, y0, z0, cols, rows, zDim)
        continue
      }
      scratch.length = 0
      for (let k = -kMax; k <= kMax; k++) {
        scratch.push(
          sample(
            getSlice,
            x0 + k * n[0],
            y0 + k * n[1],
            z0 + k * n[2],
            cols,
            rows,
            zDim
          )
        )
      }
      dest[i] = projectSamples(proj, scratch)
    }
  }
  return dest
}

/**
 * Espaciado mm a lo largo de la normal (índices → mm).
 * @param {[number,number,number]} n
 * @param {{ sx: number, sy: number, sz: number }} spacing
 */
export function spacingAlongNormalMm(n, spacing) {
  const sx = Number(spacing && spacing.sx) || 1
  const sy = Number(spacing && spacing.sy) || 1
  const sz = Number(spacing && spacing.sz) || 1
  const nx = Number(n && n[0]) || 0
  const ny = Number(n && n[1]) || 0
  const nz = Number(n && n[2]) || 0
  const L = Math.sqrt((nx * sx) ** 2 + (ny * sy) ** 2 + (nz * sz) ** 2)
  return L > 0 ? L : 1
}

/** Escala de salida durante gesto de rotación (preview). */
export const OBLIQUE_PREVIEW_SCALE = 0.5

/**
 * Reduce outCols/outRows y escala u/v para conservar el FoV del plano full.
 * @param {{ outCols: number, outRows: number }} out
 * @param {[number,number,number]} u
 * @param {[number,number,number]} v
 * @param {number} [scale]
 */
export function applyPreviewScale(out, u, v, scale = OBLIQUE_PREVIEW_SCALE) {
  const fullCols = Math.max(1, Number(out && out.outCols) || 1)
  const fullRows = Math.max(1, Number(out && out.outRows) || 1)
  const s = Math.max(0.2, Math.min(1, Number(scale) || 1))
  if (s >= 0.999) {
    return {
      outCols: fullCols,
      outRows: fullRows,
      u: u || [1, 0, 0],
      v: v || [0, 1, 0],
      preview: false,
      scale: 1,
    }
  }
  const outCols = Math.max(32, Math.round(fullCols * s))
  const outRows = Math.max(32, Math.round(fullRows * s))
  const fC = (fullCols - 1) / Math.max(1, outCols - 1)
  const fR = (fullRows - 1) / Math.max(1, outRows - 1)
  const uu = u || [1, 0, 0]
  const vv = v || [0, 1, 0]
  return {
    outCols,
    outRows,
    u: [uu[0] * fC, uu[1] * fC, uu[2] * fC],
    v: [vv[0] * fR, vv[1] * fR, vv[2] * fR],
    preview: true,
    scale: s,
  }
}

/**
 * Empaqueta cortes Int16 en un solo buffer (z-major).
 * @param {(z:number)=>Int16Array|null|undefined} getSlice
 * @param {number} cols
 * @param {number} rows
 * @param {number} zDim
 * @returns {Int16Array}
 */
export function packVolumeSlices(getSlice, cols, rows, zDim) {
  const c = Math.max(1, cols | 0)
  const r = Math.max(1, rows | 0)
  const z = Math.max(1, zDim | 0)
  const plane = c * r
  const buf = new Int16Array(plane * z)
  for (let zi = 0; zi < z; zi++) {
    const slice = getSlice(zi)
    if (!slice) continue
    const n = Math.min(plane, slice.length)
    buf.set(
      slice.subarray ? slice.subarray(0, n) : slice,
      zi * plane
    )
  }
  return buf
}

/**
 * Reslice desde volumen empaquetado (misma semántica que resliceObliquePlane).
 * @param {object} args — igual que resliceObliquePlane pero `volume: Int16Array` en vez de getSlice
 */
export function resliceObliquePlanePacked(args) {
  const cols = Math.max(1, Number(args.cols) || 1)
  const rows = Math.max(1, Number(args.rows) || 1)
  const zDim = Math.max(1, Number(args.zDim) || 1)
  const plane = cols * rows
  const volume = args.volume
  const getSlice = (zi) => {
    if (!volume) return null
    const z = Math.max(0, Math.min(zDim - 1, zi | 0))
    const start = z * plane
    return volume.subarray(start, start + plane)
  }
  return resliceObliquePlane({ ...args, getSlice })
}
